/**
 * Termínové okno výpisu — kanonická pravidla časové dimenze.
 *
 * Od řezu 2 má katalog JEDINOU časovou dimenzi: okno `dateFrom`–`dateTo` plus tolerance.
 * Všechno ostatní (svátek, měsíc) je jen ZKRATKA, která do toho okna něco předvyplní —
 * a právě ty zkratky si dřív pravidla opisovaly. Bydlí proto tady, ne u svátků (ty jsou
 * jen jedním z jejich zdrojů) a ne u dočasného shimu zaniklých parametrů na webu: ten
 * zanikl, protože staré `?month=`/`?holiday=` web čte jako neznámé (F5.2, řez 02), a
 * hlavní pobídka stránky měsíce na `monthDateWindow` stojí dál.
 *
 * Výpočty jsou čisté — dnešek chodí zvenčí, aby šly testovat bez posunu času (mantinel
 * vize J7). Jediná výjimka je `customerToday()`, která ten dnešek VYRÁBÍ a i tak si
 * „teď" bere parametrem.
 */
import { shiftIsoDate } from "./utils";

/** Termínové okno tak, jak ho nese adresa výpisu (`?dateFrom=&dateTo=`). */
export interface DateWindow {
  dateFrom: string;
  dateTo: string;
}

/**
 * Dnešek očima ZÁKAZNÍKA — pražský den, ne UTC.
 *
 * Workers i Pages běží v UTC, takže mezi 00:00 a 02:00 pražského času je UTC „dnes"
 * pořád včerejšek. Odsud plyne konkrétní škoda: 1. 9. v 00:30 Prahy by se `?month=8`
 * překlopil na okno končící 31. 8. — den, který `/api/trips` (ten pražský den zná)
 * považuje za minulý — a stránka srpna by mluvila o měsíci, který skončil.
 *
 * `sv-SE` proto, že formátuje ISO tvarem. Pravidlo bydlí ve sdíleném balíčku, protože
 * platí pro OBĚ strany: API ho mělo v `lib/dateUtils.ts` jako `pragueToday()`, web ho
 * neměl vůbec a počítal `toISOString()`.
 */
export function customerToday(now: Date = new Date()): string {
  return now.toLocaleDateString("sv-SE", { timeZone: "Europe/Prague" });
}

/**
 * Okno `{from,to}` → termín pro datumová pole, se začátkem OŘÍZNUTÝM na dnešek.
 *
 * Ořez je součást pravidla, ne kosmetika: běžící okno (letní prázdniny v srpnu, běžící
 * měsíc u `?month=`) by jinak vyplnilo pole datem v minulosti, které `min={dnes}` na
 * inputu sám odmítá — adresní řádek by pak nesl termín, jaký formulář neumí zobrazit.
 *
 * Jediná kopie: totéž se počítalo na ČTYŘECH místech (dvakrát v překlopení zaniklých
 * parametrů, u svátkových tlačítek panelu a u odkazu na stránce měsíce), pokaždé
 * s vlastním komentářem, který to obhajoval. Rozešlo by se to v prosinci.
 *
 * Nikdy nevrátí OBRÁCENÉ okno. Dřív ta jistota stála na předpokladu „volající proběhlá
 * okna neposílá", jenže stránka měsíce dostává okna z API — tedy vybraná proti JINÉMU
 * dnešku, který navíc leží až dvě minuty v edge keši. Stačilo, aby se odpověď postavila
 * v poslední den okna, a odkaz vyšel jako `dateFrom=2026-09-01&dateTo=2026-08-31`:
 * `startDate >= dateFrom AND endDate <= dateTo` je nesplnitelné, takže zaručeně prázdný
 * výpis. Předpoklad, který drží jen dokud ho každý volající dodrží, tu být nesmí.
 */
export function clampWindowStart(window: { from: string; to: string }, today: string): DateWindow {
  const start = window.from < today ? today : window.from;
  return {
    dateFrom: start > window.to ? window.to : start,
    dateTo: window.to,
  };
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Poslední den měsíce; `Date.UTC` s dnem 0 = poslední den předchozího měsíce. */
function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Rok NEJBLIŽŠÍHO budoucího výskytu měsíce: `7` v srpnu 2026 je červenec 2027, `8`
 * je právě běžící srpen 2026 (odletět v něm ještě jde, takže se počítá jako budoucí).
 *
 * Vlastní export, protože „který ročník měsíce vlastně myslíme" potřebuje i stránka
 * měsíce, a to pro JINOU věc než okno termínu — vybírá podle něj svátky. Kdyby si ten
 * rok dopočítávala sama, rozešel by se s oknem, které ta samá stránka nabízí v CTA:
 * pobídka by mířila na červenec 2027 a karta prázdnin nad ní na červenec 2026.
 */
export function nextMonthOccurrenceYear(month: number, today: string): number {
  const todayYear = Number(today.slice(0, 4));
  const todayMonth = Number(today.slice(5, 7));
  return month >= todayMonth ? todayYear : todayYear + 1;
}

/**
 * Okno celého N-tého měsíce, a to NEJBLIŽŠÍHO budoucího: `?month=7` v srpnu 2026
 * znamená červenec 2027, ne právě uplynulý.
 *
 * Běžící měsíc se počítá jako budoucí, jen se začátek okna ořízne přes `clampWindowStart`.
 */
export function monthDateWindow(month: number, today: string): DateWindow {
  const year = nextMonthOccurrenceYear(month, today);
  return clampWindowStart(
    {
      from: `${year}-${pad(month)}-01`,
      to: `${year}-${pad(month)}-${pad(lastDayOfMonth(year, month))}`,
    },
    today,
  );
}

/**
 * EFEKTIVNÍ okno filtru termínu: zadané `dateFrom`–`dateTo` rozšířené tolerancí
 * (řez 02, vize E1/E3), tak jak podle něj `/api/trips` výpis skutečně filtruje.
 *
 * - Tolerance rozšiřuje jen ZADANÝ kraj; nezadaný kraj zůstává otevřený (`undefined`).
 * - Posunutý začátek se nikdy nedostane před `today`: do minulosti se nehledá.
 *
 * Jediná kopie pravidla pro obě strany: API z něj filtruje (`toDateDimensionFilter`),
 * web z něj skládá období, které karta výpisu předá detailu (`listingDeparturePeriod`).
 * Dokud žilo dvakrát, stačilo změnit toleranci nebo ořez jen v jedné kopii a detail by
 * nepředvybral odjezd, kvůli kterému karta ve výpisu je (řez 01, AK24).
 *
 * PŘEDPOKLAD: data jsou platná ISO data nebo prázdný řetězec (sanitoval je parser filtrů
 * nebo datumové pole) — `shiftIsoDate` by nad nesmyslem vyhodil `RangeError`.
 */
export function toleranceDateWindow(
  state: { dateFrom: string; dateTo: string; dateTolerance: number },
  today: string,
): Partial<DateWindow> {
  const tolerance = state.dateTolerance;
  const from = state.dateFrom ? shiftIsoDate(state.dateFrom, -tolerance) : undefined;
  return {
    dateFrom: from === undefined ? undefined : from < today ? today : from,
    dateTo: state.dateTo ? shiftIsoDate(state.dateTo, tolerance) : undefined,
  };
}
